/**
 * DiagnosticosPage - N129 fase 2
 *
 * Lista e baixa os logs de diagnostico que o app offline sobe sozinho no sync.
 * Sem esta tela o dado chega no servidor e fica invisivel (so via GET da API).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Download, RefreshCw, Stethoscope } from 'lucide-react';
import { MainLayout } from '../components/MainLayout';
import useAuthStore from '../stores/authStore';
import {
  Diagnostico,
  baixarDiagnostico,
  formatBytes,
  listarDiagnosticos,
} from '../services/diagnosticoService';

const PAGE_SIZE = 20;

const DiagnosticosPage: React.FC = () => {
  const { user } = useAuthStore();
  const isMaster = user?.role === 'MASTER';

  const [items, setItems] = useState<Diagnostico[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [baixando, setBaixando] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listarDiagnosticos(page, PAGE_SIZE);
      setItems(data.items);
      setTotal(data.total);
      setTotalPages(data.total_pages || 1);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Falha ao carregar os diagnosticos');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    if (isMaster) carregar();
  }, [isMaster, carregar]);

  const handleDownload = async (id: number) => {
    setBaixando(id);
    setError(null);
    try {
      await baixarDiagnostico(id);
    } catch {
      setError(`Falha ao baixar o diagnostico #${id}`);
    } finally {
      setBaixando(null);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

  if (!isMaster) {
    return (
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
        <MainLayout>
          <div className="p-6">
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-center gap-2 text-amber-800 dark:text-amber-400">
              <AlertCircle size={16} />
              Apenas usuarios MASTER podem ver os logs de diagnostico.
            </div>
          </div>
        </MainLayout>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <MainLayout>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Stethoscope className="text-blue-600 dark:text-blue-400" />
                  Diagnosticos do App
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Extratos de log que os auditores enviam automaticamente no sync ({total})
                </p>
              </div>
              <button
                onClick={carregar}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                Atualizar
              </button>
            </div>
          </div>

          {error && (
            <div className="mx-6 mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* Tabela */}
          <div className="flex-1 overflow-auto px-6 py-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    {['Recebido', 'Auditor', 'Arquivo', 'Linhas', 'Tamanho', 'Versao', 'Maquina', ''].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading && items.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                        Carregando...
                      </td>
                    </tr>
                  )}
                  {!loading && items.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                        Nenhum diagnostico recebido ainda. O envio so comeca depois que a
                        auditora atualiza o app e faz o primeiro sync com internet.
                      </td>
                    </tr>
                  )}
                  {items.map((d) => (
                    <tr key={d.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap">
                        {formatDate(d.created_at)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200">
                        {d.usuario_nome || `#${d.usuario_id}`}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {d.arquivo_origem}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200">{d.linhas}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap">
                        {formatBytes(d.tamanho_bytes)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {d.app_version}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {d.device_name} {d.plataforma && `(${d.plataforma})`}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleDownload(d.id)}
                          disabled={baixando === d.id}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                        >
                          <Download size={14} className={baixando === d.id ? 'animate-bounce' : ''} />
                          {baixando === d.id ? 'Baixando...' : 'Baixar'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginacao */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 text-sm text-gray-600 dark:text-gray-400">
                <span>
                  Pagina {page} de {totalPages} — {total} registros
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1 || loading}
                    className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages || loading}
                    className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50"
                  >
                    Proxima
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </MainLayout>
    </div>
  );
};

export default DiagnosticosPage;
